import React, { useEffect, useMemo, useState } from 'react';
import { CircularProgress } from '@mui/material';
import { t } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import './ProjectCreationWaitModal.css';

const STAGE_ROTATION_INTERVAL_MS = 3500;
const PRODUCER_STAGE_COUNT = 21;

const shuffledStageOrder = (stageCount: number): number[] => {
  const order = Array.from({ length: stageCount }, (_, index) => index);

  for (let index = order.length - 1; index > 0; index -= 1) {
    const randomIndex = Math.floor(Math.random() * (index + 1));
    [order[index], order[randomIndex]] = [order[randomIndex], order[index]];
  }

  return order;
};

interface ProjectCreationWaitModalProps {
  open: boolean;
}

const ProjectCreationWaitModal: React.FC<ProjectCreationWaitModalProps> = ({ open }) => {
  const { i18n } = useLingui();
  const producerStages = useMemo(() => [
    t`Writing script notes nobody asked for`,
    t`Casting leads and one inexplicably famous cameo`,
    t`Presenting to investors with suspiciously optimistic charts`,
    t`Negotiating a bigger craft-services budget`,
    t`Waiting for one last round of notes`,
    t`Convincing everyone this was the plan all along`,
    t`Small delay, day-dreaming about what to do with all that Netflix $$$`,
    t`Union strike. And another one.`,
    t`Lead actor decided to reconnect with nature`,
    t`Finding a location that has not already been turned into luxury apartments`,
    t`Explaining that golden hour is not an all-day service`,
    t`Waiting for the director to say "one more take" just once`,
    t`Turning a coffee run into a budget line item`,
    t`Asking the weather to respect the shooting schedule`,
    t`Reassuring the investors that the third helicopter was essential`,
    t`Searching for the missing hard drive and everyone's inner peace`,
    t`Checking whether the fog machine has feelings`,
    t`Making the call sheet look like a work of fiction`,
    t`Finding out why the prop sword is suddenly in accounting`,
    t`Scheduling the wrap party before anyone notices we are behind schedule`,
    t`Bribing the printer with kind words and fresh toner`,
  ], [i18n.locale]);
  const [stageOrder, setStageOrder] = useState<number[]>(() => shuffledStageOrder(PRODUCER_STAGE_COUNT));
  const [stagePosition, setStagePosition] = useState(0);
  const title = t`Producing your project...`;

  useEffect(() => {
    if (!open) {
      setStageOrder(shuffledStageOrder(producerStages.length));
      setStagePosition(0);
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setStagePosition((currentPosition) => {
        if (currentPosition + 1 < producerStages.length) return currentPosition + 1;

        setStageOrder(shuffledStageOrder(producerStages.length));
        return 0;
      });
    }, STAGE_ROTATION_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [open, producerStages.length]);

  if (!open) return null;

  return (
    <div className="project-creation-wait-overlay" role="presentation">
      <section
        className="project-creation-wait-modal"
        role="status"
        aria-live="polite"
        aria-label={title}
      >
        <CircularProgress className="project-creation-wait-spinner" size={42} aria-hidden="true" />
        <h2>{title}</h2>
        <p key={stageOrder[stagePosition]} className="project-creation-wait-stage">
          {producerStages[stageOrder[stagePosition]]}
        </p>
      </section>
    </div>
  );
};

export default ProjectCreationWaitModal;
